import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, ChevronUpIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash';
import { compact } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import { ProfileAddressTimeline } from 'dashboard/components/ReportResult/Profile/ProfileAddressTimeline';
import { ProfileInputDetails } from 'dashboard/components/ReportResult/Profile/ProfileInputDetails';
import { SsnMasker } from 'dashboard/components/SsnMasker';
import { formatAddress, formatDate } from 'dashboard/lib/utils/formatters';
import {
  IdentityRecord,
  ReportProfile,
  ReportTemplateProfileAttributeKey,
} from 'dashboard/models/report/profile';
import Badge from 'design-system/components/Badge';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading, Paragraph, Span } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';

enum FilterType {
  All = 0,
  Info = 1,
  DriversLicenses = 2,
  AddressHistory = 3,
}

interface ProfileProps {
  multiCardFormat: boolean;
  report: ReportProfile;
}

export const Profile = ({ multiCardFormat, report }: ProfileProps) => {
  const [selectedIdentityIndex, setSelectedIdentityIndex] = useState<number>(0);
  const [selectedFilters, setSelectedFilters] = useState<Set<FilterType>>(
    new Set([FilterType.All]),
  );
  const identityRecords = report.attributes?.['identity-records'] || [];
  const selectedIdentity = identityRecords?.[selectedIdentityIndex];
  const permittedAttributes = report.attributes?.['meta']?.['permitted-attributes'] || [];

  const toggleFilters = (filterType: FilterType) => {
    if (filterType === FilterType.All) {
      setSelectedFilters(new Set([FilterType.All]));
      return;
    }

    const selectedFiltersCopy = new Set(selectedFilters);

    if (selectedFilters.has(filterType) && selectedFilters.size > 1) {
      selectedFiltersCopy.delete(filterType);
    } else {
      selectedFiltersCopy.delete(FilterType.All);
      selectedFiltersCopy.add(filterType);
    }

    setSelectedFilters(selectedFiltersCopy);
  };

  const content = (
    <Container>
      <SideNav>
        {report.attributes?.input && compact(Object.values(report.attributes.input)).length > 0 && (
          <SideNavWrapper border={!isEmpty(identityRecords)}>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              Input
            </Paragraph>
            <Spacer height={2} />
            <ProfileInputDetails input={report.attributes.input} />
            <Spacer height={2} />
          </SideNavWrapper>
        )}
        {!isEmpty(identityRecords) && (
          <SideNavWrapper>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              Identity matches
            </Paragraph>
            <Spacer height={2} />
            <ul>
              {identityRecords.map((_, index) => {
                return (
                  <li key={`identity-record-${index}`}>
                    <IdMatchNavButton
                      id={`profile-report__select-identity-${index}`}
                      selected={selectedIdentityIndex === index}
                      onClick={() => setSelectedIdentityIndex(index)}
                      data-chromatic='ignore'
                      tabIndex={0}
                    >
                      {`${index + 1}. ${formatTitle(identityRecords[index])}`}
                    </IdMatchNavButton>
                  </li>
                );
              })}
            </ul>
          </SideNavWrapper>
        )}
      </SideNav>
      <MainContent>
        {!isEmpty(identityRecords) && (
          <ExpandedIdentity>
            <Heading size='sm' data-chromatic='ignore'>
              {formatTitle(selectedIdentity)}
            </Heading>
            <FilterBar>
              <Button
                id={`profile-report__identity-${selectedIdentityIndex}-filter-all`}
                variant={selectedFilters.has(FilterType.All) ? 'primary' : 'basic'}
                width='auto'
                size='sm'
                onClick={() => toggleFilters(FilterType.All)}
              >
                All
              </Button>
              <Button
                id={`profile-report__identity-${selectedIdentityIndex}-filter-info`}
                variant={selectedFilters.has(FilterType.Info) ? 'primary' : 'basic'}
                width='auto'
                size='sm'
                onClick={() => toggleFilters(FilterType.Info)}
              >
                Info
              </Button>
              <Button
                id={`profile-report__identity-${selectedIdentityIndex}-filter-dl`}
                variant={selectedFilters.has(FilterType.DriversLicenses) ? 'primary' : 'basic'}
                width='auto'
                size='sm'
                onClick={() => toggleFilters(FilterType.DriversLicenses)}
              >
                Drivers licenses
              </Button>
              <Button
                id={`profile-report__identity-${selectedIdentityIndex}-filter-address`}
                variant={selectedFilters.has(FilterType.AddressHistory) ? 'primary' : 'basic'}
                width='auto'
                size='sm'
                onClick={() => toggleFilters(FilterType.AddressHistory)}
              >
                Address history
              </Button>
            </FilterBar>
            {(selectedFilters.has(FilterType.All) || selectedFilters.has(FilterType.Info)) && (
              <Section>
                <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                  Info
                </Paragraph>
                <InfoContent>
                  <dt>Full name</dt>
                  <dd>
                    <PermittedAttribute
                      permittedAttributes={permittedAttributes}
                      attributeKey='name_full'
                    >
                      <AttributeToggle
                        key={`profile-name-full-${selectedIdentityIndex}`}
                        id={`profile-name-full-${selectedIdentityIndex}`}
                        items={selectedIdentity.names.map((name) => name?.['name-full'])}
                      />
                    </PermittedAttribute>
                  </dd>
                  <dt>Birthdate</dt>
                  <dd>
                    <PermittedAttribute
                      permittedAttributes={permittedAttributes}
                      attributeKey='birthdate'
                    >
                      <AttributeToggle
                        key={`profile-birthdates-${selectedIdentityIndex}`}
                        id={`profile-birthdates-${selectedIdentityIndex}`}
                        items={selectedIdentity.birthdates.map((birthdate) =>
                          formatDate(birthdate),
                        )}
                      />
                    </PermittedAttribute>
                  </dd>
                  <dt>SSN</dt>
                  <dd>
                    <PermittedAttribute
                      permittedAttributes={permittedAttributes}
                      attributeKey='birthdate'
                    >
                      <AttributeToggle
                        key={`profile-ssn-${selectedIdentityIndex}`}
                        id={`profile-ssn-${selectedIdentityIndex}`}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        items={selectedIdentity?.['social-security-numbers'].map((ssn) => (
                          <SsnMasker key={ssn} ssn={ssn} />
                        ))}
                      />
                    </PermittedAttribute>
                  </dd>
                  <dt>Deceased</dt>
                  <dd>{selectedIdentity.deceased ? 'Yes' : 'No'}</dd>
                  <dt>Phone number</dt>
                  <dd>
                    <PermittedAttribute
                      permittedAttributes={permittedAttributes}
                      attributeKey='phone_number'
                    >
                      <AttributeToggle
                        key={`profile-phone-${selectedIdentityIndex}`}
                        id={`profile-phone-${selectedIdentityIndex}`}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        items={selectedIdentity?.['phone-numbers'].map((phone) => (
                          <span key={`phone-${phone['phone-number']}`}>
                            {phone['phone-number']}
                            {(phone['date-first-seen'] || phone['date-last-seen']) && (
                              <PhoneDateSpan color='secondary'>
                                {formatDateRange(phone['date-first-seen'], phone['date-last-seen'])}
                              </PhoneDateSpan>
                            )}
                          </span>
                        ))}
                      />
                    </PermittedAttribute>
                  </dd>
                  <dt>Email address</dt>
                  <dd>
                    <PermittedAttribute
                      permittedAttributes={permittedAttributes}
                      attributeKey='email_address'
                    >
                      <AttributeToggle
                        key={`profile-email-${selectedIdentityIndex}`}
                        id={`profile-email-${selectedIdentityIndex}`}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        items={selectedIdentity?.['email-addresses']}
                      />
                    </PermittedAttribute>
                  </dd>
                </InfoContent>
              </Section>
            )}
            {(selectedFilters.has(FilterType.All) ||
              selectedFilters.has(FilterType.DriversLicenses)) && (
              <Section>
                <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                  Drivers licenses
                  {!isEmpty(selectedIdentity['drivers-licenses']) && (
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    <Badge>{selectedIdentity['drivers-licenses'].length}</Badge>
                  )}
                </Paragraph>
                {isEmpty(selectedIdentity['drivers-licenses'])
                  ? 'N/A'
                  : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    selectedIdentity['drivers-licenses'].map((driversLicense, dlIdx) => (
                      <DriversLicenseContent
                        key={`drivers-license-${selectedIdentityIndex}-${dlIdx}`}
                        hasTopBorder={dlIdx !== 0}
                      >
                        <dd>Name</dd>
                        <dt>{driversLicense['name-full'] || 'N/A'}</dt>
                        <dd>Birthdate</dd>
                        <dt>{formatDate(driversLicense['birthdate']) || 'N/A'}</dt>
                        <dd>ID number</dd>
                        <dt>{driversLicense['drivers-license-number'] || 'N/A'}</dt>
                        <dd style={{ gridColumnStart: 3 }}>Issuing State</dd>
                        <dt style={{ gridColumnStart: 4 }}>
                          {driversLicense['issuing-state'] || 'N/A'}
                        </dt>
                        <dd style={{ gridColumnStart: 3 }}>Reported date</dd>
                        <dt style={{ gridColumnStart: 4 }}>
                          {formatDate(driversLicense['reported-date']) || 'N/A'}
                        </dt>
                        <dd style={{ gridColumnStart: 3 }}>Address</dd>
                        <dt style={{ gridColumnStart: 4 }}>
                          {formatAddress({
                            addressStreet1: driversLicense.address?.['street-1'],
                            addressStreet2: driversLicense.address?.['street-2'],
                            addressCity: driversLicense.address?.city,
                            addressSubdivision: driversLicense.address?.subdivision,
                            addressPostalCode: driversLicense.address?.['postal-code'],
                            color: 'primary',
                          })}
                        </dt>
                      </DriversLicenseContent>
                    ))}
              </Section>
            )}
            {(selectedFilters.has(FilterType.All) ||
              selectedFilters.has(FilterType.AddressHistory)) && (
              <Section>
                <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                  Address history
                  {!isEmpty(selectedIdentity.addresses) && (
                    <Badge>{selectedIdentity.addresses.length}</Badge>
                  )}
                </Paragraph>
                <PermittedAttribute
                  permittedAttributes={permittedAttributes}
                  attributeKey='address'
                >
                  {isEmpty(selectedIdentity.addresses) ? (
                    'N/A'
                  ) : (
                    <ProfileAddressTimeline
                      key={`address-timeline-${selectedIdentityIndex}`}
                      addresses={selectedIdentity.addresses}
                    />
                  )}
                </PermittedAttribute>
              </Section>
            )}
            <Spacer height={2} />
            {/*These law only apply to US or PR but Profile Report only works in US and PR so no need
                to put additional conditionals for now*/}
            <Paragraph>
              Results shall only be used for Gramm-Leach-Bliley Act (GLBA) permissible use cases
              such as identity verification for the purpose of fraud prevention. Additionally, the
              information provided does not constitute and may not be used as a &ldquo;consumer
              report&rdquo; as is defined in the Fair Credit Reporting Act (&ldquo;FCRA&rdquo;).
            </Paragraph>
          </ExpandedIdentity>
        )}
      </MainContent>
    </Container>
  );

  return multiCardFormat ? <Card>{content}</Card> : content;
};

const formatTitle = (identity: IdentityRecord) => {
  const name = identity.names?.[0]['name-full'];
  const birthdate = identity.birthdates?.[0];
  const yearsPassed = birthdate
    ? Math.floor(DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years)
    : null;

  return name + (yearsPassed ? `, ${yearsPassed}` : '');
};

const formatDateRange = (first: string, last: string) => {
  if (first === last) {
    return formatDate(first);
  }

  return `${first ? formatDate(first) : 'unknown'} to ${last ? formatDate(last) : 'unknown'}`;
};

const PermittedAttribute = ({
  children,
  permittedAttributes,
  attributeKey,
}: {
  children: ReactNode | string;
  permittedAttributes: ReportTemplateProfileAttributeKey[];
  attributeKey: ReportTemplateProfileAttributeKey;
}) => {
  if (!permittedAttributes.includes(attributeKey)) {
    return <Sticker>Restricted</Sticker>;
  } else {
    return <>{children}</>;
  }
};

export const AttributeToggle = ({
  id,
  items,
}: {
  id: string;
  items: (React.ReactNode | string)[];
}) => {
  const [isOpen, { toggle: toggleAttribute }] = useToggle(false);

  if (!items || !items.length) {
    return <>N/A</>;
  } else if (items.length === 1) {
    return <>{items?.[0]}</>;
  } else {
    return (
      <AttributeToggleButton id={id} onClick={() => toggleAttribute()}>
        <ul style={{ overflowY: isOpen ? 'scroll' : undefined }}>
          <AttributeToggleItemFirst key='first' showIcon={isOpen}>
            <span>{items[0]}</span>
            <Badge>{items.length}</Badge>
            {!isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
          </AttributeToggleItemFirst>
          {isOpen &&
            items
              .filter((_, idx) => idx > 0)
              .map((item, idx) => <PlainListItem key={idx}>{item}</PlainListItem>)}
        </ul>
      </AttributeToggleButton>
    );
  }
};

const AttributeToggleButton = styled.button`
  background-color: transparent;
  text-align: left;
  cursor: pointer;
`;

const AttributeToggleItemFirst = styled.li<{ showIcon: boolean }>`
  list-style-type: none;
  display: flex;
  align-items: center;
  grid-gap: ${Spacing[1]};

  & > svg {
    display: none;
  }

  &:hover {
    & > svg {
      display: block;
    }
  }

  ${({ showIcon }) =>
    showIcon &&
    css`
      & > svg {
        display: block;
      }
    `}
`;

const IdMatchNavButton = styled.button<{ selected: boolean }>`
  padding: ${Spacing[2]};
  width: 100%;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  ${paragraphMixins['md']};
  background-color: transparent;
  text-align: left;

  &:hover {
    cursor: pointer;
  }

  &:hover,
  &:active,
  &:focus {
    color: ${({ theme }) => theme.color.primary.brand};
    background-color: ${({ theme }) => theme.color.primary.brand100};
  }

  ${({ selected }) =>
    selected &&
    css`
      color: ${({ theme }) => theme.color.primary.brand};
      background-color: ${({ theme }) => theme.color.primary.brand100};
    `}
`;

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 300px auto;
`;

const SideNav = styled.div`
  grid-row: span 2;
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const SideNavWrapper = styled.section<{ border?: boolean }>`
  padding: ${Spacing[3]} ${Spacing[4]};

  & > ul > li {
    list-style-type: none;
  }

  ${({ border }) =>
    border &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
    `}
`;

const MainContent = styled.div`
  padding: ${Spacing[5]};
  grid-row: 2;
  grid-column: 2;
`;

const ExpandedIdentity = styled.div`
  display: grid;
  grid-gap: ${Spacing[2]};
`;

const FilterBar = styled.div`
  display: flex;
  grid-gap: ${Spacing[2]};
`;

const Section = styled.section`
  border-radius: 5px;
  border: 1px solid ${Color.CoreSlate200};
  padding: ${Spacing[4]};
  & > h4 {
    margin-bottom: ${Spacing[3]};
    display: flex;
    align-items: center;
    grid-gap: ${Spacing[1]};
  }
`;

const PlainListItem = styled.li`
  list-style-type: none;
`;

const PhoneDateSpan = styled(Span)`
  padding-left: ${Spacing[1]};
  ${paragraphMixins['sm']}
`;

const InfoContent = styled.dl`
  display: grid;
  grid-gap: ${Spacing[2]};
  grid-template-columns: repeat(2, 1fr);

  & dt {
    color: ${({ theme }) => `${theme.color.text.secondary}`};
  }
`;

const DriversLicenseContent = styled.dl<{ hasTopBorder: boolean }>`
  display: grid;
  grid-auto-flow: column;
  grid-gap: ${Spacing[2]};
  grid-template-columns: repeat(4, 1fr);
  padding: ${Spacing[3]} 0;

  ${({ hasTopBorder }) =>
    hasTopBorder &&
    css`
      border-top: 1px solid ${({ theme }) => theme.color.border.primary};
    `}

  & dd {
    grid-column: 1;
    color: ${({ theme }) => `${theme.color.text.secondary}`};
  }

  & dt {
    grid-column: 2;
  }
`;
